import { dropLast, take } from "ramda";

import moment from "moment";
import moment_tz from "moment-timezone";

import { ActionCreator, createActions, ICreateActionsOptions } from "reduxsauce";

import { encode } from "query-params";

import { Units } from "humanize-duration";

export const MAX_FILE_SIZE = 10000000;

export function insertAtPath(path: string, value: any, target: { [k: string]: any }): void {
  const keys = path.split(".");
  let pointedAt = target;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (i === keys.length - 1) {
      pointedAt[key] = value;
    } else if (!pointedAt[key]) {
      pointedAt[key] = {};
    }
    pointedAt = pointedAt[key];
  }
}

export function mergeActionDefinitions(
  namespace: string,
  ownActionDefinitions: { [k: string]: string[] },
  ...childActionDefinitions: Array<{ [k: string]: string[] }>
): { [k: string]: string[] } {
  const result = {};
  Object.entries(ownActionDefinitions).forEach(([actionKey, actionDefinition]) => {
    result[`${namespace}.${actionKey}`] = actionDefinition;
  });
  childActionDefinitions.forEach(childActionDefinition => {
    Object.entries(childActionDefinition).forEach(([actionKey, actionDefinition]) => {
      result[`${namespace}.${actionKey}`] = actionDefinition;
    });
  });
  return result;
}

export interface ICreatorMapping {
  [k: string]: ActionCreator | ICreatorMapping;
}

export interface ITypeMapping {
  [k: string]: string | ITypeMapping;
}

export function actionDefinitionsToActions(
  actionDefinitions: { [k: string]: string[] },
  options: ICreateActionsOptions
): { Creators: ICreatorMapping; Types: ITypeMapping } {
  const sauceResult = createActions(actionDefinitions, options);
  const Creators = {};
  Object.entries(sauceResult.Creators).forEach(([key, creator]) => {
    insertAtPath(key, creator, Creators);
  });
  const Types = {};
  Object.entries(sauceResult.Types).forEach(([key, type]) => {
    insertAtPath(`_${key}`, type, Types);
  });
  return { Creators, Types };
}

export function jsonToFormUrlencoded(jsonData: { [k: string]: undefined | null | number | string | boolean }): string {
  return encode(jsonData);
}

export function getPercentage(n: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((100 * n) / total);
}

export function average(list: number[]): number {
  let sum = 0;
  list.forEach(n => {
    sum += n;
  });
  const totalElements = list.length || 1;
  return sum / totalElements;
}

export function format(time: number): string {
  const aux = take(
    2,
    moment
      .duration(time, "s")
      .humanize()
      .split(" ")
  ).join(" ");
  if (aux === "a few") {
    return "0";
  } else if (aux === "a minute") {
    return "1 min";
  }
  return dropLast(4, aux);
}

export function max(numbers: number[]): number {
  // We are using this forEach because Math.max has a limit regarding it's
  // polyvariadic nature, so it will fail for the longest courses.
  // The ramda version involves more memory used, which has a detrimental
  // effect on the memory usage
  // return reduce<number, number>(rMax, -Infinity, numbers);
  let res = numbers.length === 0 ? 0 : -Infinity;
  numbers.forEach(n => {
    if (n > res) {
      res = n;
    }
  });
  return res;
}

export function min(numbers: number[]): number {
  // We are using this forEach because Math.min has a limit regarding it's
  // polyvariadic nature, so it will fail for the longest courses.
  // The ramda version involves more memory used, which has a detrimental
  // effect on the memory usage
  // return reduce<number, number>(rMin, Infinity, numbers);
  let res = numbers.length === 0 ? 0 : Infinity;
  numbers.forEach(n => {
    if (n < res) {
      res = n;
    }
  });
  return res;
}

export const humanizeFormat = (nanoSeconds: number): IHumanizeFormat => {
  const oneHourInNanoseconds = 3600000;
  if (nanoSeconds > oneHourInNanoseconds) {
    return { largest: 2, units: ["h", "m"] };
  }
  return { largest: 1, units: ["m", "s"] };
};

interface IHumanizeFormat {
  largest: number;
  units: Units[];
}

interface IModifiableElem {
  id: string;
  url: string;
}

export function getSkinUrls(
  apiRoot: string,
  organizationId: string,
  version: string,
  fetchTimestamp?: string
): { logo: IModifiableElem; skin: IModifiableElem } {
  const q = fetchTimestamp ? `?q=${fetchTimestamp}` : "";
  return {
    logo: {
      id: "dashboard-logo",
      url: `${apiRoot}/shc/images/logos/${organizationId}${q}`,
    },
    skin: {
      id: "organization-skin-stylesheet",
      url: `${apiRoot}/assets/styles/${organizationId}/${version}/custom.css${q}`,
    },
  };
}

export function readableDateTime(isoDateString: string): string {
  const date = moment(isoDateString);

  if (!date.isValid()) {
    return isoDateString;
  }

  return date.format("h:mm:ss a MMM D, YYYY");
}

export function timeSince(isoDateString: string): string {
  const date = moment(isoDateString);
  const now = moment();

  if (!date.isValid()) {
    return isoDateString;
  }

  return moment.duration(now.diff(date)).humanize();
}

export function fmtDateToLocalZone(isoDateString: string): string {
  const date = moment_tz(isoDateString);
  const tz = moment_tz.tz.guess();

  if (!date.isValid()) {
    return isoDateString;
  }

  return date
    .local()
    .tz(tz)
    .format("DD/MM/YYYY h:mm A z");
}

export const pluralize = (text: string, amount: number, customEnding = "s"): string =>
  amount !== 1 ? `${amount} ${text}${customEnding}` : `1 ${text}`;

export const contains = (lookupText: string, fullText: string): boolean => {
  return fullText.toLowerCase().includes(lookupText.toLowerCase());
};

export const localeCompare = (a: string, b: string): boolean => a.localeCompare(b) < 0;

export const getSortableHelperContainer = () => document.getElementById("react-sortable-hoc-helper") || document.body;

export const isNullOrUndefined = <T>(a: T): boolean => a === null || a === undefined;

export const objectMap = <T extends {}>(obj: T, fn: any) =>
  Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v, k, i)]));
